import React, { useRef, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { AddReaction, DeleteOutline, EditOutlined, MoreVert } from '@mui/icons-material';
import { IMessage } from '../../redux/messages/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

interface MessageActionsProps {
    message: IMessage;
    isCurrentUser: boolean;
    onReactionClick: (emoji: string, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onDeleteMessage?: (messageId: string) => void;
    onEditMessage?: (messageId: string, newContent: string) => void;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
    message,
    isCurrentUser,
    onReactionClick,
    onDeleteMessage,
    onEditMessage,
}) => {
    const { themeStyle } = useThemeContext();
    const actionButtonRef = useRef<HTMLButtonElement>(null);
    const reactionButtonRef = useRef<HTMLButtonElement>(null);

    // Menu states
    const [showReactionMenu, setShowReactionMenu] = useState(false);
    const [showActionsMenu, setShowActionsMenu] = useState(false);

    // Common reaction emojis
    const commonReactions = ['👍', '❤️', '😂', '😮', '😢', '👏', '🔥', '🎉'];

    // Check if the user has already reacted with a specific emoji
    const hasUserReactedWith = (emoji: string) => {
        return message.reactions?.some(r => r.reaction === emoji && r.user === message.created_by._id);
    };

    const handleOpenReactionMenu = (e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setShowReactionMenu(true);
    };

    const closeReactionMenu = () => {
        setShowReactionMenu(false);
    };

    const handleReactionSelect = (emoji: string, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        // Prevent default and stop propagation to avoid scrolling
        e.preventDefault();
        e.stopPropagation();

        // Pass the emoji directly
        onReactionClick(emoji, e);
        closeReactionMenu();
    };

    const handleOpenActionsMenu = (e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setShowActionsMenu(true);
    };

    const closeActionsMenu = () => {
        setShowActionsMenu(false);
    };

    const handleDeleteMessage = () => {
        if (onDeleteMessage) {
            onDeleteMessage(message._id);
        }
        closeActionsMenu();
    };

    const handleEditMessage = () => {
        // Only allow editing for 'chat' type messages
        if (message.message_type !== 'chat' || !onEditMessage) return;

        closeActionsMenu();
        onEditMessage(message._id, message.message_content || '');
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                    ref={reactionButtonRef}
                    size="small"
                    onClick={handleOpenReactionMenu}
                    sx={{
                        color: isCurrentUser ? 'rgba(255, 255, 255, 0.5)' : theme[themeStyle].grey,
                        p: 0,
                    }}>
                    <AddReaction fontSize="small" />
                </IconButton>
                {isCurrentUser && (
                    <IconButton
                        ref={actionButtonRef}
                        size="small"
                        onClick={handleOpenActionsMenu}
                        sx={{
                            color: isCurrentUser ? 'rgba(255, 255, 255, 0.5)' : theme[themeStyle].grey,
                            p: 0,
                        }}>
                        <MoreVert fontSize="small" />
                    </IconButton>
                )}
            </Box>

            {/* Reaction menu */}
            <Menu
                open={showReactionMenu}
                anchorEl={reactionButtonRef.current}
                onClose={closeReactionMenu}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: 4,
                        bgcolor: theme[themeStyle].deep_grey,
                        boxShadow: 3,
                    },
                }}
                MenuListProps={{
                    sx: { py: 0 },
                }}>
                <Box sx={{ display: 'flex', p: 1, gap: 0.5 }}>
                    {commonReactions.map(emoji => (
                        <IconButton
                            key={emoji}
                            onClick={e => handleReactionSelect(emoji, e)}
                            title={hasUserReactedWith(emoji) ? 'Click to remove reaction' : 'Add reaction'}
                            sx={{
                                p: 1,
                                fontSize: '1.2rem',
                                borderRadius: '50%',
                                backgroundColor: message.reactions?.some(
                                    r => r.reaction === emoji && r.user === message.created_by._id,
                                )
                                    ? `${theme[themeStyle].highlight}30`
                                    : 'transparent',
                                '&:hover': {
                                    bgcolor: theme[themeStyle].secondaryBg,
                                },
                            }}>
                            {emoji}
                        </IconButton>
                    ))}
                </Box>
            </Menu>

            {/* Message actions menu */}
            <Menu
                open={showActionsMenu}
                anchorEl={actionButtonRef.current}
                onClose={closeActionsMenu}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        bgcolor: theme[themeStyle].deep_grey,
                        color: theme[themeStyle].tag_white,
                        borderRadius: 2,
                        minWidth: 150,
                    },
                }}>
                {/* Only show edit option for chat messages */}
                {message.message_type === 'chat' && onEditMessage && (
                    <MenuItem onClick={handleEditMessage} dense>
                        <EditOutlined fontSize="small" sx={{ mr: 1 }} />
                        Edit Message
                    </MenuItem>
                )}
                {/* Show delete option for all message types */}
                <MenuItem onClick={handleDeleteMessage} dense>
                    <DeleteOutline fontSize="small" sx={{ mr: 1, color: '#f44336' }} />
                    <Typography sx={{ color: '#f44336' }}>Delete Message</Typography>
                </MenuItem>
            </Menu>
        </>
    );
};
